import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchGalleryBySlug, getOptimizedImageUrl } from '../lib/cms';
import { Shield, Flame, TrendingUp, Cpu, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Industri({ isEn }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGallery() {
      try {
        const data = await fetchGalleryBySlug('industri');
        setGallery(data);
      } catch (error) {
        console.error('Error loading industri gallery:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, []);

  const lang = isEn ? 'en' : 'da';

  const seoTitle = isEn 
    ? 'Industrial Insulation - Fredericia Isolerings-puder' 
    : 'Industriisolering - Fredericia Isolerings-puder';
  const seoDesc = isEn 
    ? 'Tailored insulation pads for industrial process plants, CHP plants, and factories. Reduce heat loss and energy consumption.'
    : 'Skræddersyede isoleringspuder til industrielle procesanlæg, kraftvarmeværker og fabrikker. Reducer varmetab og energiforbrug.';

  const points = isEn ? [
    'Up to 1,300°C temperature resistance for extreme industrial environments.',
    'Significant reduction in gas and energy consumption (proven results).',
    'Rapid mounting and dismounting in minutes reduces maintenance downtime.',
    'Protects personnel from contact injuries with hot machinery parts.',
    'Bespoke production tailored precisely to pipes, valves, and boilers.',
    '100% produced with green energy from our own solar plant.'
  ] : [
    'Op til 1.300°C temperaturbestandighed til ekstreme industrimiljøer.',
    'Markant reduktion i gas- og energiforbrug (dokumenterede resultater).',
    'Hurtig montering og afmontering på få minutter minimerer driftstop.',
    'Beskytter personale mod kontaktulykker med varme maskindele.',
    'Skræddersyet produktion tilpasset præcis til rør, ventiler og kedler.',
    '100% produceret med grøn strøm fra vores eget solcelleanlæg.'
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-brand-dark overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:5rem_5rem] opacity-10" />
        <div className="absolute right-0 top-0 h-full w-1/3 bg-brand-accent transform -skew-x-12 translate-x-12" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="w-full text-left space-y-4">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Industrial Insulation' : 'Industriisolering'}
            </h1>
            <div className="pt-4">
              <Link 
                to={isEn ? '/en/contact' : '/kontakt'} 
                className="inline-block bg-brand-accent hover:bg-white hover:text-black text-white font-display font-bold text-sm uppercase tracking-wider px-8 py-4 transition-colors rounded-none"
              >
                {isEn ? 'Get a Quote' : 'Få et uforpligtende tilbud'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Benefits */}
      <section className="py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 text-left">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 tracking-tight">
              {isEn ? 'Optimize Efficiency & Reduce Energy Loss' : 'Optimer effektiviteten & reducer energitab'}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              {isEn 
                ? 'Industrial plants lose massive amounts of energy through uninsulated or poorly insulated pipes, valves, and boilers. Traditional metal cladding is often left off after maintenance because it is difficult to reinstall. Our insulation pads solve this problem completely.'
                : 'Industrielle anlæg mister enorme mængder energi gennem uisolerede eller dårligt isolerede rør, ventiler og kedler. Traditionel metalindkapsling bliver ofte udeladt efter vedligeholdelse, fordi den er svær at genmontere. Vores isoleringspuder løser dette problem fuldstændigt.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {points.map((point, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{point}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/20250519-130915-scaled.jpg', { width: 600 })}
                alt="Industriisolering"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dynamisk Galleri via CMS-reference */}
      <section className="py-20 bg-brand-dark border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Industrial Installation Gallery' : 'Industrielt installationsgalleri'}
            </h2>
            <p className="text-[#f1f1f1] text-sm mt-2">
              {isEn 
                ? 'Explore our high-quality industrial insulation installations. Automatically updated from our CMS.'
                : 'Udforsk vores industrielle isoleringsinstallationer af høj kvalitet. Opdateres automatisk fra vores CMS.'}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
            </div>
          ) : gallery?.images && gallery.images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {gallery.images.map((imgUrl, idx) => (
                <div 
                  key={idx} 
                  className="relative aspect-square rounded-lg overflow-hidden border border-gray-800 bg-brand-surface group hover:border-gray-700 transition-all duration-300"
                >
                  <img
                    src={getOptimizedImageUrl(imgUrl, { width: 400, height: 400, fit: 'cover' })}
                    alt={`Industriisolering ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 font-mono text-xs">
              {isEn ? 'No gallery images found.' : 'Ingen galleribilleder fundet.'}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-surface relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center px-4 sm:px-6 lg:px-8 space-y-6 relative z-10">
          <h2 className="text-3xl font-display font-bold text-white tracking-tight">
            {isEn ? 'Need a Customized Industrial Solution?' : 'Har du brug for en skræddersyet industriløsning?'}
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            {isEn 
              ? 'We design and produce insulation pads specifically for your machinery, temperature, and environment. Contact us today for a free consultation.'
              : 'Vi designer og producerer isoleringspuder specifikt til dine maskiner, temperaturer og omgivelser. Kontakt os i dag for en uforpligtende samtale.'}
          </p>
          <div className="pt-2">
            <Link
              to={isEn ? '/en/contact' : '/kontakt'}
              className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accent-hover text-white font-medium px-6 py-3.5 rounded transition-colors shadow-lg shadow-brand-accent/10"
            >
              {isEn ? 'Contact Our Team' : 'Kontakt vores team'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
